import styled from '@emotion/styled';
import {
  PushpinOutlined,
  PictureOutlined,
  HolderOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
} from '@ant-design/icons';
import { getRankingEffectDisplayName, ProductScoreDto, RankingEffectEnum } from 'interfaces/apiTypes';

export type ProductRankingCardVariant = 'default' | 'pinned';

interface ProductRankingCardProps {
  item: ProductScoreDto;
  rank: number;
  variant?: ProductRankingCardVariant;
  isDragging?: boolean;
  dropLine?: 'above' | 'below' | null;
  canMoveUp?: boolean;
  canMoveDown?: boolean;
  onPin?: (productScoreId: number) => void;
  onUnpin?: (productScoreId: number) => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  draggable?: boolean;
  onDragStart?: (e: React.DragEvent<HTMLDivElement>) => void;
  onDragOver?: (e: React.DragEvent<HTMLDivElement>) => void;
  onDrop?: (e: React.DragEvent<HTMLDivElement>) => void;
  onDragEnd?: (e: React.DragEvent<HTMLDivElement>) => void;
}

export const getEffectLabel = (item: ProductScoreDto) => {
  if (item.isPinned || item.appliedEffect === RankingEffectEnum.Pin) {
    const pinLabel = getRankingEffectDisplayName(RankingEffectEnum.Pin);
    if (item.pinnedManually) {
      return `${pinLabel} (manuellt)`;
    }
    return `${pinLabel} (regel)`;
  }
  if (item.appliedEffect == null) {
    return 'Organisk';
  }
  return getRankingEffectDisplayName(Number(item.appliedEffect));
};

const PIN = {
  rowBg: '#fffdf3',
  rowHover: '#fff8e1',
  rowDragging: '#fff1b8',
  rowBorder: '#fff3cd',
  outerBorder: '#ffe58f',
  heading: '#874d00',
  secondary: '#ad6800',
  accent: '#d48806',
  handle: '#d4b106',
} as const;

const Row = styled.div<{
  $variant: ProductRankingCardVariant;
  $dragging?: boolean;
  $dropLine?: 'above' | 'below' | null;
}>`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 8px 16px;
  width: 100%;
  transition: background 0.15s ease, box-shadow 0.12s ease, opacity 0.12s ease;
  border-bottom: 1px solid
    ${({ $variant }) => ($variant === 'pinned' ? PIN.rowBorder : '#f5f5f5')};
  background: ${({ $variant, $dragging }) => {
    if ($variant === 'pinned') {
      return $dragging ? PIN.rowDragging : PIN.rowBg;
    }
    return '#fff';
  }};
  cursor: ${({ $variant }) => ($variant === 'pinned' ? 'grab' : 'default')};
  opacity: ${({ $dragging }) => ($dragging ? 0.85 : 1)};
  box-shadow: ${({ $dropLine }) => {
    if ($dropLine === 'above') return `inset 0 3px 0 0 ${PIN.accent}`;
    if ($dropLine === 'below') return `inset 0 -3px 0 0 ${PIN.accent}`;
    return 'none';
  }};

  &:hover {
    background: ${({ $variant, $dragging }) => {
      if ($dragging) return PIN.rowDragging;
      return $variant === 'pinned' ? PIN.rowHover : '#fafcff';
    }};
  }

  &:last-child {
    border-bottom: none;
  }
`;

const DragHandle = styled.span<{ $pinned?: boolean }>`
  width: 15px;
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${({ $pinned }) => ($pinned ? PIN.handle : 'transparent')};
  font-size: 12px;
`;

const RankBadge = styled.span<{ $pinned?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  flex: none;
  background: ${({ $pinned }) => ($pinned ? '#fffbe6' : '#f5f5f5')};
  border: 1px solid ${({ $pinned }) => ($pinned ? PIN.outerBorder : 'transparent')};
  color: ${({ $pinned }) => ($pinned ? PIN.accent : '#595959')};
`;

const Thumb = styled.div<{ $pinned?: boolean }>`
  width: 36px;
  height: 36px;
  border-radius: 6px;
  background: ${({ $pinned }) => ($pinned ? '#fff8e1' : '#f0f0f0')};
  border: 1px solid ${({ $pinned }) => ($pinned ? PIN.outerBorder : '#e8e8e8')};
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  color: #bfbfbf;
  font-size: 9px;
`;

const ThumbImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const Info = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

const Name = styled.span<{ $pinned?: boolean }>`
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${({ $pinned }) => ($pinned ? PIN.heading : '#141414')};
`;

const Meta = styled.span<{ $pinned?: boolean }>`
  font-size: 12px;
  color: ${({ $pinned }) => ($pinned ? PIN.secondary : '#8c8c8c')};
`;

const Scores = styled.div`
  display: flex;
  gap: 24px;
  flex: none;
  text-align: right;
`;

const ScoreBlock = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 76px;

  &.ranking {
    min-width: 96px;
  }
`;

const ScoreLabel = styled.span<{ $pinned?: boolean }>`
  font-size: 11px;
  color: ${({ $pinned }) => ($pinned ? PIN.secondary : '#8c8c8c')};
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const ScoreValue = styled.span<{ $pinned?: boolean }>`
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  color: ${({ $pinned }) => ($pinned ? PIN.heading : '#141414')};

  &.ranking {
    font-weight: 600;
    color: ${({ $pinned }) => ($pinned ? PIN.accent : '#d4380d')};
  }
`;

const Actions = styled.div<{ $pinned?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  flex: none;
  /* Keep scores aligned whether "Ta bort nål" is shown or not */
  min-width: ${({ $pinned }) => ($pinned ? '168px' : 'auto')};
`;

const ArrowGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex: none;
`;

const UNPIN_BUTTON_WIDTH = 98;

const UnpinPlaceholder = styled.span`
  display: inline-block;
  width: ${UNPIN_BUTTON_WIDTH}px;
  height: 26px;
  flex: none;
`;

const IconButton = styled.button<{ $pinned?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border: 1px solid ${({ $pinned }) => ($pinned ? PIN.outerBorder : 'transparent')};
  border-radius: 5px;
  background: ${({ $pinned }) => ($pinned ? '#fff' : 'transparent')};
  color: ${({ $pinned }) => ($pinned ? PIN.accent : '#8c8c8c')};
  cursor: pointer;
  padding: 0;
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease;

  &:hover:not(:disabled) {
    background: ${({ $pinned }) => ($pinned ? '#fffbe6' : '#f0f5fb')};
    border-color: ${({ $pinned }) => ($pinned ? PIN.accent : 'transparent')};
    color: ${({ $pinned }) => ($pinned ? PIN.accent : '#14395e')};
  }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
`;

const ActionButton = styled.button<{ $pinned?: boolean }>`
  display: flex;
  align-items: center;
  gap: 5px;
  height: 26px;
  padding: 0 9px;
  border: 1px solid ${({ $pinned }) => ($pinned ? PIN.outerBorder : '#d9d9d9')};
  border-radius: 5px;
  background: #fff;
  color: ${({ $pinned }) => ($pinned ? PIN.accent : '#595959')};
  font-family: inherit;
  font-size: 12px;
  cursor: pointer;
  flex: none;
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease;

  &:hover:not(:disabled) {
    border-color: ${PIN.accent};
    color: ${PIN.accent};
    background: #fffbe6;
  }

  &.danger {
    border-color: #d9d9d9;
    color: #595959;
    background: #fff;
  }

  &.danger:hover:not(:disabled) {
    border-color: #ffa39e;
    color: #cf1322;
    background: #fff1f0;
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

export const ProductRankingCard = ({
  item,
  rank,
  variant = 'default',
  isDragging = false,
  dropLine = null,
  canMoveUp = false,
  canMoveDown = false,
  onPin,
  onUnpin,
  onMoveUp,
  onMoveDown,
  draggable,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
}: ProductRankingCardProps) => {
  const pinned = variant === 'pinned';
  const score = pinned || item.isPinned ? 1200 : item.currentPopularityScore;
  const canUnpin = Boolean(item.pinnedManually && onUnpin);

  return (
    <Row
      $variant={variant}
      $dragging={isDragging}
      $dropLine={dropLine}
      draggable={draggable}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
    >
      <DragHandle $pinned={pinned} aria-hidden>
        {pinned ? <HolderOutlined /> : null}
      </DragHandle>

      <RankBadge $pinned={pinned}>{rank}</RankBadge>

      <Thumb $pinned={pinned}>
        {item.imageUrl ? (
          <ThumbImage src={item.imageUrl} alt={item.name ?? ''} />
        ) : (
          <PictureOutlined style={{ fontSize: 14, color: pinned ? PIN.handle : '#bfbfbf' }} />
        )}
      </Thumb>

      <Info>
        <Name $pinned={pinned} title={item.name}>
          {item.name}
        </Name>
        <Meta $pinned={pinned}>
          {item.mainItemNo} · {getEffectLabel(item)}
        </Meta>
      </Info>

      <Scores>
        <ScoreBlock>
          <ScoreLabel $pinned={pinned}>Baspoäng</ScoreLabel>
          <ScoreValue $pinned={pinned}>{item.basePopularityScore}</ScoreValue>
        </ScoreBlock>
        <ScoreBlock className="ranking">
          <ScoreLabel $pinned={pinned}>Rankingpoäng</ScoreLabel>
          <ScoreValue $pinned={pinned} className="ranking">
            {score}
          </ScoreValue>
        </ScoreBlock>
      </Scores>

      <Actions $pinned={pinned}>
        {pinned ? (
          <>
            <ArrowGroup>
              <IconButton
                type="button"
                $pinned
                title="Flytta upp"
                disabled={!canMoveUp || isDragging}
                onClick={(e) => {
                  e.stopPropagation();
                  onMoveUp?.();
                }}
              >
                <ArrowUpOutlined />
              </IconButton>
              <IconButton
                type="button"
                $pinned
                title="Flytta ner"
                disabled={!canMoveDown || isDragging}
                onClick={(e) => {
                  e.stopPropagation();
                  onMoveDown?.();
                }}
              >
                <ArrowDownOutlined />
              </IconButton>
            </ArrowGroup>
            {canUnpin ? (
              <ActionButton
                type="button"
                $pinned
                className="danger"
                disabled={isDragging}
                style={{ minWidth: UNPIN_BUTTON_WIDTH, justifyContent: 'center' }}
                onClick={(e) => {
                  e.stopPropagation();
                  onUnpin?.(item.id);
                }}
              >
                Ta bort nål
              </ActionButton>
            ) : (
              <UnpinPlaceholder aria-hidden />
            )}
          </>
        ) : (
          <ActionButton type="button" onClick={() => onPin?.(item.id)}>
            <PushpinOutlined style={{ fontSize: 12 }} />
            Nåla fast
          </ActionButton>
        )}
      </Actions>
    </Row>
  );
};
